/**
 * HDS cart upsell — the drawer's one "next product" card.
 *
 * A colour with no developer in the basket leads with its developer (the one-tap card from
 * assets/cart-dev-popout.js); otherwise the co-purchase map (assets/cart-upsell-map.json),
 * else nothing at all. Developer-first is owner, 2026-10-19 (approved from the mockup), and
 * REVERSES the 2026-10-05 "no peroxide in the minicart" ruling: 480 colour-without-developer
 * sessions were being shown foil (0.7% add rate) instead.
 * Owner rules (spec 2026-10-01): full price only; never a product already in the cart;
 * never MORE professional than the most professional thing in the cart.
 *
 * Pure logic first so tests/cart-upsell.test.mjs can evaluate THIS file in a VM.
 */
(function () {
  'use strict';

  function handleOf(item) { return (item && typeof item.handle === 'string') ? item.handle : ''; }

  /** 0 for an empty cart. A handle the map cannot class is ignored (fail closed). */
  function cartLevel(items, map) {
    var levels = (map && map.levels) || {};
    var max = 0;
    (items || []).forEach(function (i) {
      var h = handleOf(i);
      if (!h) return;
      if (!levels[h]) return;  // skip unclassified
      var l = levels[h];
      if (l > max) max = l;
    });
    return max;
  }

  /**
   * Swaps that apply to this cart: {replacedHandle: replacementHandle}. A swap fires when the
   * cart holds 1..max_units of its colour products (summed across shade lines) and none of its
   * `unless` handles (owner, 2026-10-12: a few tubes = a home job, so foil becomes the tint
   * bowl + brush set).
   */
  function activeSwaps(lines, map, inCart) {
    var out = {};
    ((map && map.swaps) || []).forEach(function (s) {
      if (!s || !s['with']) return;
      var colour = {}, units = 0;
      (s.colour || []).forEach(function (h) { colour[h] = true; });
      lines.forEach(function (i) { if (colour[handleOf(i)]) units += Number(i.quantity) || 0; });
      if (units < 1 || units > (s.max_units || 0)) return;
      if ((s.unless || []).some(function (h) { return inCart[h]; })) return;
      (s.replace || []).forEach(function (h) { if (!out[h]) out[h] = s['with']; });
    });
    return out;
  }

  function pickMap(items, map, isAvailable, limit) {
    var max = limit || 4;
    var partners = (map && map.partners) || {};
    var levels = (map && map.levels) || {};
    var lines = (items || []).filter(function (i) { return handleOf(i); });
    var inCart = {};
    lines.forEach(function (i) { inCart[handleOf(i)] = true; });
    var swaps = activeSwaps(lines, map, inCart);
    var ceiling = cartLevel(lines, map);
    var totals = {};
    lines.forEach(function (i) {
      var h = handleOf(i);
      totals[h] = (totals[h] || 0) + (Number(i.final_line_price) || 0);
    });
    var sources = Object.keys(totals).sort(function (a, b) { return totals[b] - totals[a]; });
    var out = [], taken = {};
    // A swap's product already in the cart: never suggest its parts (`unless`) beside it
    // (owner, 2026-10-12: the set must never sit next to the cheaper single bowl / brush).
    ((map && map.swaps) || []).forEach(function (sw) {
      if (sw && inCart[sw['with']]) (sw.unless || []).forEach(function (h) { taken[h] = true; });
    });
    for (var s = 0; s < sources.length && out.length < max; s++) {
      var list = partners[sources[s]] || [];
      for (var p = 0; p < list.length && out.length < max; p++) {
        var h = swaps[list[p]] || list[p];
        if (inCart[h] || taken[h]) continue;
        if (!levels[h] || levels[h] > ceiling) continue;
        if (!isAvailable(h)) continue;
        taken[h] = true;
        var c = { handle: h, source: sources[s], rank: p + 1 };
        if (h !== list[p]) c.swapped_from = list[p];
        out.push(c);
      }
    }
    return out;
  }

  function gate(audience, arm, isStaff) {
    if (audience === 'debug') return !!isStaff;
    if (audience === 'test') return arm === 'on';
    return audience === 'all';
  }

  function armFor(audience, attr) {
    if (audience !== 'test') return null;
    return (attr === 'on' || attr === 'control') ? attr : 'none';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return c.charCodeAt(0) === 39 ? '&#39;' : { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function gbp(pence) { return '£' + (Number(pence) / 100).toFixed(2); }

  /**
   * The price cell. It uses the product-listing markup b2b-pricing.js already fills
   * (`.priceWraper[data-product-handle]`), so a trade shopper's tier price comes from
   * the same price book as every listing card. A trade shopper gets NO number until
   * that lands - the spec's rule is no price rather than a retail one.
   */
  function priceMarkup(handle, pence, isTrade) {
    var inner = isTrade ? '' : '<span class="pr_price">' + gbp(pence) + '</span>';
    return '<div class="priceWraper cu-price' + (isTrade ? ' cu-price--pending' : '') +
           '" data-product-handle="' + esc(handle) + '">' + inner + '</div>';
  }

  /**
   * The minicart offers single-variant products only (owner, 2026-10-05): no size or shade
   * picker, one tap adds exactly what the card names. The map is built that way; this is the
   * guard for a stale map or a product that has since gained variants.
   */
  function singleOffer(p) {
    var vs = (p && p.variants) || [];
    return vs.length === 1 && vs[0].available ? vs[0] : null;
  }

  /**
   * "More options" = products SIMILAR to the one shown (owner, 2026-10-05), from the map's
   * `similar` list (vault substitutes, then same category). Never in the basket, never the
   * main product, max 3, in map order.
   */
  function similarFor(map, main, items) {
    var inCart = {};
    (items || []).forEach(function (i) { var h = handleOf(i); if (h) inCart[h] = true; });
    var hidden = {};  // a swap product in the cart hides its parts here too (see pickMap)
    ((map && map.swaps) || []).forEach(function (sw) {
      if (sw && inCart[sw['with']]) (sw.unless || []).forEach(function (h) { hidden[h] = true; });
    });
    var list = (map && map.similar && map.similar[main]) || [];
    // Every eligible one, in map order: the caller checks stock and keeps the first 3.
    return list.filter(function (h) { return h !== main && !inCart[h] && !hidden[h]; });
  }

  /**
   * Will a tier/segment price replace the public one for this shopper? Asks b2b-pricing.js
   * (the canonical gate); before it has loaded, the same cookie test it uses. A pending card
   * renders no number until the tier price lands - never the retail one.
   */
  function tierPriced() {
    var g = typeof window !== 'undefined' && window.B2BListingPrices;
    if (g && typeof g.pending === 'function') return !!g.pending();
    return typeof document !== 'undefined' &&
      /(?:^|;\s*)(?:b2b_tier|src_(?:campaign|segment))=[^;]+/.test(document.cookie || '');
  }

  function cardKey(c) {
    if (!c) return '';
    return 'map|' + (c.handles || []).join(',');
  }

  // State identity (More open, quantities): the source line, so a reshuffle of picks keeps it.
  function stateKeyFor(c) {
    if (!c) return '';
    return 'map|' + (c.source || '');
  }

  // ---------------------------------------------------------------------------------------
  // Tracking (owner, 2026-10-06): what was offered where, More options opens, strength picks,
  // adds and failed adds go to the site's metrics collector (Laravel analytics_events) for
  // the upsell dashboard. Best-effort: nothing here may ever block a render or an add.
  // ---------------------------------------------------------------------------------------
  var COLLECT = 'https://metrics.hairdressingsupplies.com/collect';
  var pixelIds = { session_id: null, visitor_id: null };
  var seenOffers = {};

  /** The price-list tag in the b2b_tier cookie (plus / partner / wholesale), else 'retail'. */
  function tierFromCookie(cookie) {
    var m = /(?:^|;\s*)b2b_tier=([^;]+)/.exec(cookie || '');
    if (!m) return 'retail';
    var tags = decodeURIComponent(m[1]).toLowerCase().split(/\s*,\s*/);
    var order = ['wholesale', 'partner', 'plus'];
    for (var i = 0; i < order.length; i++) if (tags.indexOf(order[i]) !== -1) return order[i];
    return 'trade';
  }

  /** The collector envelope for one event. Pure: ctx = {now, rand, ids, customerId, ua, page, tier}. */
  function trackPayload(name, fields, ctx) {
    var f = fields || {};
    var upsell = {};
    for (var k in f) if (Object.prototype.hasOwnProperty.call(f, k)) upsell[k] = f[k];
    upsell.tier = ctx.tier || 'retail';
    var items = (f.offered || (f.handle ? [{ handle: f.handle, variant_id: f.variant_id, price_pence: f.price_pence }] : []))
      .map(function (o) {
        return { item_id: o.handle, item_variant: o.variant_id != null ? String(o.variant_id) : null,
                 price: o.price_pence != null ? Number(o.price_pence) / 100 : null, quantity: f.qty || 1 };
      });
    return {
      event_name: name,
      event_id: name + '_' + ctx.now + '_' + ctx.rand,
      client_name: 'Shopify Theme',
      timestamp: new Date(ctx.now).toISOString(),
      timestamp_unix: ctx.now,
      session: { session_id: (ctx.ids && ctx.ids.session_id) || null },
      visitor: { visitor_id: (ctx.ids && ctx.ids.visitor_id) || null },
      page: ctx.page || {},
      device: { user_agent: ctx.ua || '' },
      user_data: { customer_id: ctx.customerId || null },
      ecommerce: { currency: 'GBP', items: items },
      upsell: upsell
    };
  }

  /** Send one event. An impression is sent once per offer_id per page view. */
  function track(name, fields) {
    try {
      if (name === 'upsell_impression') {
        var key = fields && fields.offer_id;
        if (!key || seenOffers[key]) return false;
        seenOffers[key] = true;
      }
      var w = typeof window !== 'undefined' ? window : {};
      var d = typeof document !== 'undefined' ? document : {};
      var loc = w.location || {};
      var body = JSON.stringify(trackPayload(name, fields, {
        now: Date.now(), rand: Math.random().toString(36).slice(2, 11), ids: pixelIds,
        customerId: w.__b2b_customer_id || null, ua: (w.navigator && w.navigator.userAgent) || '',
        page: { path: loc.pathname || '', location: loc.href || '', title: d.title || '' },
        tier: tierFromCookie(d.cookie)
      }));
      var nav = w.navigator || {};
      if (nav.sendBeacon) nav.sendBeacon(COLLECT, body);
      else if (w.fetch) w.fetch(COLLECT, { method: 'POST', body: body, keepalive: true, mode: 'no-cors' }).catch(function () {});
      return true;
    } catch (e) { return false; }
  }

  // The pixel's session/visitor ids are only readable from its postMessage broadcast.
  try {
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('message', function (e) {
        var p = e && e.data && e.data.type === 'hsuk_pixel_event' && e.data.payload;
        if (!p) return;
        if (p.session_id) pixelIds.session_id = p.session_id;
        if (p.visitor_id) pixelIds.visitor_id = p.visitor_id;
      });
    }
  } catch (e) { /* no window messaging: events still carry customer id */ }

  window.CartUpsell = { cartLevel: cartLevel, pickMap: pickMap, gate: gate, armFor: armFor,
                        priceMarkup: priceMarkup, esc: esc, gbp: gbp, cardKey: cardKey, stateKeyFor: stateKeyFor,
                        singleOffer: singleOffer, similarFor: similarFor, tierPriced: tierPriced,
                        trackPayload: trackPayload, track: track, tierFromCookie: tierFromCookie };

  if (typeof document === 'undefined' || !document.addEventListener) return;
  var cfg = window.__cartUpsell || {};
  var mapP = null, devMapP = null, productCache = {}, timer = null, lastKey = '', busy = false;
  var mountNode = null, stateKey = '', lastMap = null;
  var state = { more: false, qty: {} };
  // Basket + stock context for the quantity selector (same rules as the pop-out).
  var ctx = { stock: {}, inCart: {} };
  var stockCache = {};

  // Shared pure helpers live in the pop-out (assets/cart-dev-popout.js, same gate); the
  // fallbacks only matter if that script were missing.
  function H() { return window.CartDevPopout || {}; }
  function capFor(stock, inCart) {
    if (H().capFor) return H().capFor(stock, inCart);
    return (stock === null || stock === undefined) ? 99 : Math.max(0, Math.min(99, stock - (inCart || 0)));
  }
  function maxOf(id) { return capFor(ctx.stock[id], ctx.inCart[id]); }

  function stock(h) {
    return stockCache[h] || (stockCache[h] = fetch('/products/' + h + '?section_id=api-variant-stock', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (t) { var m = /data-variant-stock>([^<]*)</.exec(t || ''); return m ? JSON.parse(m[1]) : {}; })
      .catch(function () { return {}; }));
  }
  function stocks(handles) {
    return Promise.all(handles.map(stock)).then(function (ss) {
      ss.forEach(function (m) { for (var k in m) if (Object.prototype.hasOwnProperty.call(m, k)) ctx.stock[k] = m[k]; });
    });
  }
  function setBasket(items) {
    ctx.inCart = {};
    (items || []).forEach(function (it) { ctx.inCart[it.variant_id] = (ctx.inCart[it.variant_id] || 0) + (it.quantity || 0); });
  }
  /** Copies of product payloads with sold-through variants (all stock already in the basket) unavailable. */
  function capped(prods) {
    var out = {};
    for (var h in prods) {
      if (!Object.prototype.hasOwnProperty.call(prods, h)) continue;
      var p = prods[h];
      out[h] = p ? Object.assign({}, p, { variants: (p.variants || []).map(function (v) {
        return Object.assign({}, v, { available: !!v.available && maxOf(v.id) > 0 });
      }) }) : null;
    }
    return out;
  }

  function stepper(key) {
    var q = state.qty[key] || (state.qty[key] = 1), k = esc(key), max = maxOf(key);
    return '<div class="cu__qty" role="group" aria-label="Quantity">' +
      '<button type="button" class="cu__qb" data-cu-dec="' + k + '" aria-label="Fewer"' + (q <= 1 ? ' disabled' : '') + '>\u2212</button>' +
      '<span class="cu__qn" data-cu-q="' + k + '" aria-live="polite">' + q + '</span>' +
      '<button type="button" class="cu__qb" data-cu-inc="' + k + '" aria-label="More"' +
      (q >= max ? ' disabled' + (max < 99 ? ' title="Only ' + max + ' available"' : '') : '') + '>+</button></div>';
  }
  /** In place: no redraw, nothing moves. */
  function bump(key, delta) {
    var max = maxOf(key);
    if (max < 1) return;
    var q = Math.max(1, Math.min(max, (state.qty[key] || 1) + delta));
    state.qty[key] = q;
    var m = el(); if (!m) return;
    var n = m.querySelector('[data-cu-q="' + key + '"]'); if (n) n.textContent = String(q);
    var dec = m.querySelector('[data-cu-dec="' + key + '"]'), inc = m.querySelector('[data-cu-inc="' + key + '"]');
    if (dec) dec.disabled = q <= 1;
    if (inc) { inc.disabled = q >= max; inc.title = (q >= max && max < 99) ? 'Only ' + max + ' available' : ''; }
  }

  function el() { return document.getElementById('dev-upsell-mount'); }
  function json(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function upsellMap() { return mapP || (mapP = cfg.mapUrl ? json(cfg.mapUrl) : Promise.resolve(null)); }
  function devMap() { return devMapP || (devMapP = cfg.devMapUrl ? json(cfg.devMapUrl) : Promise.resolve(null)); }
  // Also lets b2b-pricing.js predict the cart an upsell Add will produce and price it in
  // parallel with Shopify (window.__hdsVariantInfo, read by its variantInfo()).
  function rememberVariants(p) {
    if (p && p.variants) {
      var reg = window.__hdsVariantInfo = window.__hdsVariantInfo || {};
      p.variants.forEach(function (v) { reg[v.id] = { title: v.name, sku: v.sku || '', price: v.price, product_id: p.id, product_type: p.type || '' }; });
    }
    return p;
  }
  function product(h) { return productCache[h] || (productCache[h] = json('/products/' + h + '.js').then(rememberVariants)); }
  function products(handles) {
    return Promise.all(handles.map(product)).then(function (ps) {
      var o = {}; handles.forEach(function (h, i) { o[h] = ps[i]; }); return o;
    });
  }
  function arm() { return document.documentElement.getAttribute('data-ab-cart_upsell'); }
  function hide() { var m = el(); if (m) { m.hidden = true; m.innerHTML = ''; } lastKey = ''; }
  /**
   * Run a card render without moving the checkout footer. .drawer__inner scrolls items, card
   * and footer together, so a card that lands while the shopper is at the bottom would push
   * Checkout off screen. If the footer is on screen, scroll by however far it moved (0 when the
   * browser's own scroll anchoring already held it). Only for renders the shopper didn't ask for.
   */
  function holdFooter(fn) {
    var m = el(), sc = m && m.closest('.drawer__inner'), f = sc && sc.querySelector('.drawer__footer');
    if (!f) return fn();
    var top = f.getBoundingClientRect().top, onScreen = top < sc.getBoundingClientRect().bottom;
    var r = fn();
    if (onScreen) { var d = f.getBoundingClientRect().top - top; if (d) sc.scrollTop += d; }
    return r;
  }
  function prices() { if (window.B2BListingPrices) window.B2BListingPrices.apply(); }
  function img(src) {
    return src ? '<img class="cu__img" src="' + esc(String(src).replace(/(\.[a-z]+)(\?|$)/i, '_88x$1$2')) +
      '" alt="" width="44" height="44" loading="lazy">' : '<span class="cu__img"></span>';
  }

  function stampCart(cart) {
    var a = armFor(cfg.audience, arm());
    if (!a || !cart || !cart.item_count) return;
    if (cart.attributes && cart.attributes.ab_cart_upsell === a) return;
    // b2b_skip=1: an attributes-only write must not trigger the ~3s B2B re-sync.
    fetch('/cart/update.js?b2b_skip=1', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { ab_cart_upsell: a } })
    }).catch(function () {});
  }

  function addBtn(r, choice, mapInfo) {
    return '<button type="button" class="cu__add" data-cu-add="' + esc(r.variantId) + '" data-cu-choice="' +
      esc(choice) + '"' + (mapInfo ? ' data-cu-src="' + esc(mapInfo.source) + '" data-cu-rank="' + mapInfo.rank + '"' +
        ' data-cu-h="' + esc(mapInfo.handle) + '"' : '') +
      (r.price != null ? ' data-cu-p="' + esc(r.price) + '"' : '') + '>Add</button>';
  }

  function renderMap(main, extra, prods, names) {
    var m = el(); if (!m) return;
    if (!main) return hide();
    var cell = function (x, mini) {
      var p = prods[x.handle], v = singleOffer(p);
      // Short hand-written card name from the map; the full title only if one is missing.
      var name = (names && names[x.handle]) || p.title || '';
      var price = priceMarkup(x.handle, v.price, tierPriced());
      if (mini) {
        // No stepper on the similar rows (adds 1): the name needs the width, mobile especially.
        return '<div class="cu__mini">' + img(p.featured_image) +
          '<div class="cu__body"><span class="cu__mname">' + esc(name) + '</span></div>' + price +
          addBtn({ variantId: v.id, price: v.price }, 'map', x) + '</div>';
      }
      var act = stepper(v.id) + addBtn({ variantId: v.id, price: v.price }, 'map', x);
      // Two rows: name / price on top, then quantity + Add (+ More options) - nothing squeezes the name.
      return '<div class="cu__row">' + img(p.featured_image) +
        '<div class="cu__body"><p class="cu__name">' + esc(name) + '</p></div>' + price + '</div>' +
        '<div class="cu__act">' + act +
          (extra.length ? '<button type="button" class="cu__more" data-cu-more aria-expanded="' + state.more + '">' +
            (state.more ? 'Fewer options' : 'More options ›') + '</button>' : '') +
        '</div>';
    };
    m.innerHTML = '<div class="cu" data-cu-kind="map">' + cell(main) +
      (state.more && extra.length
        ? '<div class="cu__extras">' + extra.map(function (x) { return cell(x, true); }).join('') + '</div>' : '') +
      '</div>';
    m.hidden = false;
    prices();
    track('upsell_impression', {
      surface: 'minicart', offer_id: offerId(main), source_handle: main.source,
      offered: [main].concat(extra).map(function (x, i) {
        var v = singleOffer(prods[x.handle]) || {};
        return { handle: x.handle, variant_id: v.id, position: i, price_pence: v.price };
      })
    });
  }

  function offerId(main) { return 'minicart|' + (main && main.source) + '|' + (main && main.handle); }

  function resetStateFor(sk) {
    if (sk !== stateKey) { state = { more: false, qty: {} }; stateKey = sk; }
  }

  // Redraw from the last rendered inputs: a More click needs no network.
  function redraw() {
    if (lastMap) renderMap(lastMap.main, lastMap.extra, lastMap.prods, lastMap.names);
  }

  // ---------------------------------------------------------------------------------------
  // Developer first (see the header). dev = the drawer's developer card state, null when the
  // drawer shows the map pick instead.
  // ---------------------------------------------------------------------------------------
  var dev = null;

  function devBranch(items, dmap, umap, force) {
    var D = window.CartDeveloperUpsell, P = H();
    var choice = (D && P.cardView && dmap) ? D.pick(items, dmap) : null;
    var pair = choice && dmap.pairs[choice.colourHandle];
    if (!pair || !dmap.families || !dmap.families[pair.developer]) { dev = null; return mapBranch(items, umap, force); }
    var dkey = 'dev|' + choice.colourHandle + '|' + choice.tubes;
    if (!force && dkey === lastKey && !el().hidden) return;
    var hs = P.handlesFor(dmap, pair.developer, true);
    return Promise.all([products(hs), stocks(hs)]).then(function (both) {
      var prods = capped(both[0]);
      var offer = D.developerOffer(dmap, pair.developer, prods, { defaultStrength: pair.default_strength });
      // Their developer is sold out: the map pick, as before.
      if (!offer || !offer.strengths.length) { dev = null; return mapBranch(items, umap, force); }
      var need = P.basketNeed(dmap, items, pair.developer);
      var keep = dev && dev.colour === choice.colourHandle && offer.strengths.indexOf(dev.strength) !== -1;
      dev = { colour: choice.colourHandle, offer: offer, prods: prods, needMl: need && need.ml,
              sub: P.buttonCopy(pair, need).sub, strength: keep ? dev.strength : offer.defaultStrength, qty: {} };
      lastKey = dkey; lastMap = null;
      holdFooter(renderDev);
    });
  }

  function renderDev() {
    var m = el(), P = H();
    var v = m && dev && P.cardView(dev.offer, dev.strength, dev.needMl, dev.prods);
    if (!v) return hide();
    // One swipe card per size (owner, 2026-11-02, layout D). The drawer keeps the strength
    // chips: unlike the panel it has no pop-out to change strength in.
    var slides = v.rows.map(function (c) {
      var id = c.row.variantId, max = maxOf(id);
      dev.qty[id] = Math.max(1, Math.min(dev.qty[id] || c.qty, max || 1));
      return Object.assign({}, c, { qty: dev.qty[id], max: max, added: false,
        priceHtml: priceMarkup(c.row.handle, c.row.price, tierPriced()) });
    });
    m.innerHTML = P.cardHtml(Object.assign({}, v, { sub: dev.sub, slides: slides, chips: true, others: '' }), esc);
    m.hidden = false;
    prices();
    track('upsell_impression', { surface: 'minicart_dev', offer_id: 'minicart_dev|' + dev.colour,
      source_handle: dev.colour, strength: v.strength,
      offered: v.rows.map(function (c, i) { return { handle: c.row.handle, variant_id: c.row.variantId, position: i, price_pence: c.row.price }; }) });
  }

  /** +/- on the drawer's developer card, in place. */
  function devBump(id, delta) {
    var max = maxOf(id);
    if (max < 1 || !dev) return;
    var q = Math.max(1, Math.min(max, (dev.qty[id] || 1) + delta));
    dev.qty[id] = q;
    var m = el(); if (!m) return;
    var n = m.querySelector('[data-dvc-q="' + id + '"]'); if (n) n.textContent = String(q);
    var dec = m.querySelector('[data-dvc-dec="' + id + '"]'), inc = m.querySelector('[data-dvc-inc="' + id + '"]');
    if (dec) dec.disabled = q <= 1;
    if (inc) { inc.disabled = q >= max; inc.title = (q >= max && max < 99) ? 'Only ' + max + ' available' : ''; }
  }

  function devAdd(btn) {
    if (busy || btn.disabled || !dev) return;
    var card = btn.closest('.dvc'); if (!card) return;
    busy = true; btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('dvc__add--busy');
    btn.innerHTML = '<span class="dvc__spin" aria-hidden="true"></span><span class="dvc__vh">Adding</span>';
    var vid = btn.getAttribute('data-dvc-add'), qty = dev.qty[vid] || 1, colour = dev.colour, strength = dev.strength;
    var payload = { items: [{ id: vid, quantity: qty, properties: {
      _dev_upsell_source: colour, _dev_upsell_surface: 'minicart', _dev_upsell_choice: 'branded' } }] };
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.getSectionsToRender) {
      payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status) throw new Error(res.description || res.message || '');
        track('upsell_add', { surface: 'minicart_dev', offer_id: 'minicart_dev|' + colour, handle: btn.getAttribute('data-dvc-h') || '',
          variant_id: vid, qty: qty, group: 'branded', strength: strength, price_pence: Number(btn.getAttribute('data-dvc-p')) || null });
        // A developer is in the basket now: the next refresh falls through to the map pick.
        busy = false; lastKey = ''; dev = null;
        if (drawer && drawer.renderContents && res.sections) drawer.renderContents(res);
        schedule(true);
      })
      .catch(function (err) {
        busy = false; btn.disabled = false;
        btn.removeAttribute('aria-busy'); btn.classList.remove('dvc__add--busy'); btn.textContent = 'Add';
        var e = card.querySelector('.dvc__err');
        if (!e) { e = document.createElement('p'); e.className = 'dvc__err'; e.setAttribute('role', 'alert'); card.appendChild(e); }
        e.textContent = (err && err.message) || 'That could not be added. Please try again.';
        track('upsell_add_failed', { surface: 'minicart_dev', handle: btn.getAttribute('data-dvc-h') || '', reason: (err && err.message) || '' });
      });
  }

  function mapBranch(items, umap, force) {
    if (!umap) return hide();
    // Ranked candidates (up to 12); stock is checked a batch at a time until one can be
    // offered, so a sold-out top four never hides an in-stock fifth.
    var cand = pickMap(items, umap, function () { return true; }, 12);
    var mkey = cardKey({ kind: 'map', handles: cand.map(function (c) { return c.handle; }) });
    if (!force && mkey === lastKey && !el().hidden) return;
    var mh = cand.map(function (c) { return c.handle; });
    var prods = {}, main = null;
    // The main product is the first co-purchase pick that can be offered; "More options" are
    // products similar to IT, fetched once it is known. One render, after both.
    return firstOffered(mh, 1, 4, prods).then(function (got) {
      main = got.length ? cand.filter(function (x) { return x.handle === got[0]; })[0] : null;
      if (!main) return [];
      return firstOffered(similarFor(umap, main.handle, items), 3, 3, prods).then(function (sh) {
        return sh.map(function (h, i) { return { handle: h, source: main.source, rank: 's' + (i + 1) }; });
      });
    }).then(function (extra) {
      resetStateFor(stateKeyFor({ kind: 'map', handles: mh, source: cand[0] && cand[0].source }));
      lastKey = mkey;
      lastMap = { main: main, extra: extra, prods: prods, names: umap.names };
      holdFooter(function () { renderMap(main, extra, prods, umap.names); });
    });
  }

  /**
   * The first `need` handles (in order) that can be offered, fetching product + stock `batch`
   * at a time and stopping as soon as enough are found. Fills `prods`.
   */
  function firstOffered(handles, need, batch, prods) {
    var found = [];
    function step(i) {
      if (found.length >= need || i >= handles.length) return Promise.resolve(found);
      var hs = handles.slice(i, i + batch);
      return Promise.all([products(hs), stocks(hs)]).then(function (both) {
        Object.assign(prods, capped(both[0]));
        hs.forEach(function (h) { if (found.length < need && singleOffer(prods[h])) found.push(h); });
        return step(i + batch);
      });
    }
    return step(0);
  }

  function drawerOpen() {
    var d = document.querySelector('cart-drawer');
    return !!(d && d.classList.contains('active'));
  }

  function refresh(force) {
    if (!el() || busy) return;
    // The card only matters while the drawer is open. A closed drawer costs no requests:
    // no basket read, no maps, no product files. Opening it triggers the first refresh.
    if (!drawerOpen()) return;
    // Cheapest possible exit: off / debug-non-staff never touch the network.
    if (!gate(cfg.audience, arm(), cfg.staff) && !armFor(cfg.audience, arm())) return hide();
    // Developer first when a colour has none (see the header), else the co-purchase pick.
    Promise.all([json('/cart.js'), upsellMap(), devMap()]).then(function (r) {
      var cart = r[0], umap = r[1], dmap = r[2];
      stampCart(cart);
      if (!gate(cfg.audience, arm(), cfg.staff)) return hide();
      var items = (cart && cart.items) || [];
      setBasket(items);
      return devBranch(items, dmap, umap, force);
    }).catch(hide);
  }

  function schedule(force) { clearTimeout(timer); timer = setTimeout(function () { refresh(force); }, 250); }

  function add(btn) {
    if (busy || btn.disabled) return;
    var card = btn.closest('.cu'); if (!card) return;
    busy = true; btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('cu__add--busy');
    btn.innerHTML = '<span class="cu__spin" aria-hidden="true"></span><span class="cu__vh">Adding</span>';
    var props = { _cart_upsell_source: btn.getAttribute('data-cu-src') || '', _cart_upsell_rank: btn.getAttribute('data-cu-rank') || '' };
    var vid = btn.getAttribute('data-cu-add');
    var payload = { items: [{ id: vid, quantity: state.qty[vid] || 1, properties: props }] };
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.getSectionsToRender) {
      payload.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
      payload.sections_url = window.location.pathname;
    }
    fetch('/cart/add.js', { method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status) throw new Error(res.description || res.message || '');
        track('upsell_add', addFields(btn, vid, state.qty[vid] || 1));
        busy = false; lastKey = ''; state.more = false; state.qty = {};
        if (drawer && drawer.renderContents && res.sections) drawer.renderContents(res);
        schedule(true);
      })
      .catch(function (err) {
        busy = false; btn.disabled = false;
        btn.removeAttribute('aria-busy'); btn.classList.remove('cu__add--busy'); btn.textContent = 'Add';
        showError(card, err && err.message);
        track('upsell_add_failed', { surface: 'minicart', handle: btn.getAttribute('data-cu-h') || '', reason: (err && err.message) || '' });
      });
  }

  function addFields(btn, vid, qty) {
    var rank = btn.getAttribute('data-cu-rank') || '';
    return { surface: 'minicart', offer_id: lastMap ? offerId(lastMap.main) : '', handle: btn.getAttribute('data-cu-h') || '',
      variant_id: vid, qty: qty, position: /^s\d/.test(rank) ? Number(rank.slice(1)) : 0,
      price_pence: Number(btn.getAttribute('data-cu-p')) || null };
  }

  /** Shopify's own reason ("only 3 left" etc.) under the card; a plain retry line if none. */
  function showError(card, msg) {
    var e = card.querySelector('.cu__err');
    if (!e) { e = document.createElement('p'); e.className = 'cu__err'; e.setAttribute('role', 'alert'); card.appendChild(e); }
    e.textContent = msg || 'That could not be added. Please try again.';
  }

  function start() {
    // +/- react on pointerdown (no click-release lag); keyboard via click detail 0.
    document.addEventListener('pointerdown', function (e) {
      if (e.button !== 0 || !e.target || !e.target.closest || !el()) return;
      var vq = e.target.closest('[data-dvc-dec]') || e.target.closest('[data-dvc-inc]');
      if (vq && el().contains(vq)) {
        if (vq.disabled) return;
        e.preventDefault();
        var vdec = vq.hasAttribute('data-dvc-dec');
        return devBump(vq.getAttribute(vdec ? 'data-dvc-dec' : 'data-dvc-inc'), vdec ? -1 : 1);
      }
      var dq = e.target.closest('[data-cu-dec]'), iq = e.target.closest('[data-cu-inc]');
      var b = dq || iq;
      if (!b || b.disabled || !el().contains(b)) return;
      e.preventDefault();
      bump(b.getAttribute(dq ? 'data-cu-dec' : 'data-cu-inc'), dq ? -1 : 1);
    });
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest;
      if (!t) return;
      var card = el() && el().contains(e.target) && e.target.closest('[data-dvc]');
      if (card) {
        var vq = e.target.closest('[data-dvc-dec]') || e.target.closest('[data-dvc-inc]');
        if (vq) {
          e.preventDefault();
          var vdec = vq.hasAttribute('data-dvc-dec');
          if (e.detail === 0 && !vq.disabled) devBump(vq.getAttribute(vdec ? 'data-dvc-dec' : 'data-dvc-inc'), vdec ? -1 : 1);
          return;
        }
        var vs = e.target.closest('[data-dvc-strength]');
        if (vs && dev) {
          e.preventDefault();
          dev.strength = vs.getAttribute('data-dvc-strength');
          track('upsell_strength_pick', { surface: 'minicart_dev', offer_id: 'minicart_dev|' + dev.colour, strength: dev.strength });
          renderDev();
          var f = el().querySelector('[data-dvc-strength="' + dev.strength + '"]');
          return f && f.focus();
        }
        var va = e.target.closest('[data-dvc-add]');
        if (va) { e.preventDefault(); return devAdd(va); }
        return;
      }
      var dq = e.target.closest('[data-cu-dec]'), iq = e.target.closest('[data-cu-inc]');
      if ((dq || iq) && el() && el().contains(dq || iq)) {
        e.preventDefault();
        var qb = dq || iq;
        if (e.detail === 0 && !qb.disabled) bump(qb.getAttribute(dq ? 'data-cu-dec' : 'data-cu-inc'), dq ? -1 : 1);
        return;
      }
      var b = e.target.closest('[data-cu-add]');
      if (b && el() && el().contains(b)) { e.preventDefault(); return add(b); }
      var mo = e.target.closest('[data-cu-more]');
      if (mo && el() && el().contains(mo)) {
        e.preventDefault(); state.more = !state.more;
        if (state.more && lastMap) track('upsell_more_open', { surface: 'minicart', offer_id: offerId(lastMap.main) });
        return redraw();
      }
    });
    var drawer = document.querySelector('cart-drawer');
    // The mount lives inside .drawer__inner, which every qty change rewrites: watch the
    // drawer, re-resolve the mount each time (el() never caches), redraw when emptied.
    // The mount lives inside .drawer__inner, which every qty change rewrites. The drawer also
    // mutates every second (banner countdown), so act ONLY when the mount node itself is new.
    if (drawer) new MutationObserver(function () {
      var m = el();
      if (m !== mountNode) { mountNode = m; lastKey = ''; schedule(false); }
    }).observe(drawer, { childList: true, subtree: true });
    mountNode = el();
    // Opening the drawer (CartDrawer.open() adds .active) is the first refresh.
    if (drawer) new MutationObserver(function () {
      if (drawerOpen()) schedule(true);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('b2b:cart-synced', function () { schedule(false); });
    document.addEventListener('b2b:listing-prices', prices);
    // Heading for the basket icon: fetch the upsell maps now so the card only waits on the
    // basket + product files when the drawer opens. Once per page, same gate as refresh().
    var warmed = false;
    function warm(e) {
      if (warmed || !e.target || !e.target.closest || !e.target.closest('#cartLink, .header-cart')) return;
      warmed = true;
      if (gate(cfg.audience, arm(), cfg.staff) || armFor(cfg.audience, arm())) { upsellMap(); devMap(); }
    }
    document.addEventListener('pointerover', warm, true);
    document.addEventListener('touchstart', warm, { capture: true, passive: true });
    // Split test only: every cart must carry its arm even if the drawer is never opened, so
    // one basket read once the page is idle stamps it. No other audience reads anything here.
    if (armFor(cfg.audience, arm())) {
      // Stamp until a stamped (non-empty) cart is seen: once at idle, then after cart changes
      // (an empty cart at page load fills later, often without the drawer ever opening).
      var stamped = false;
      var ensureStamp = function () {
        if (stamped) return;
        json('/cart.js').then(function (c) {
          if (!c || !c.item_count) return;
          if (c.attributes && c.attributes.ab_cart_upsell === armFor(cfg.audience, arm())) { stamped = true; return; }
          stampCart(c); stamped = true;
        });
      };
      if (window.requestIdleCallback) window.requestIdleCallback(ensureStamp, { timeout: 4000 });
      else setTimeout(ensureStamp, 1500);
      document.addEventListener('b2b:cart-synced', ensureStamp);
      document.addEventListener('cart-notification:open', ensureStamp);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
